import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { API_URL, authApi } from '@/lib/api';
import { Bell, Plus, Trash2, Zap, Save, Loader2, QrCode as QrIcon } from 'lucide-react';

/**
 * Exhibitor-facing announcements & coupons manager.
 * - One-click "Launch 30-min Flash Deal"
 * - Full CRUD (title/body/discount/dates)
 * - Live QR preview for each coupon
 */
export default function AnnouncementsManager({ boothNumber }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [qrCache, setQrCache] = useState({}); // { announcementId: sanitizedSvg }
  const [form, setForm] = useState({
    type: 'flash_sale',
    title: '',
    body: '',
    discount_label: 'BUY 1 FREE 1',
    discount_pct: '',
    minutes: 30,
    send_push: false,
    scheduled_at: '',
    image_url: '',
    quantity_limit: '',
  });

  const authHeaders = { Authorization: `Bearer ${authApi.getToken()}`, Accept: 'application/json' };

  const notify = useCallback((msg, isError = false) => {
    setFlash({ msg, isError });
    setTimeout(() => setFlash(null), 2500);
  }, []);

  const fetchList = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/exhibitor/announcements`, { headers: authHeaders });
      setItems(res.data.data || []);
    } catch (e) {
      // Not fatal
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const launchQuick = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/exhibitor/announcements/quick-promo`, {
        minutes: Number(form.minutes) || 30,
        discount_label: form.discount_label,
      }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      notify(t('exhibitor.announcements.flash_live'));
      fetchList();
    } catch (e) {
      notify(friendlyError(e, t('exhibitor.announcements.published'), t), true);
    } finally {
      setSaving(false);
    }
  };

  const createCustom = async () => {
    if (!form.title.trim()) { notify('Title is required', true); return; }
    setSaving(true);
    try {
      const now = new Date();
      const scheduledDate = form.scheduled_at ? new Date(form.scheduled_at) : null;
      const startsAt = scheduledDate && scheduledDate > now ? scheduledDate : now;
      const expires = new Date(startsAt.getTime() + (Number(form.minutes) || 30) * 60 * 1000);
      const res = await axios.post(`${API_URL}/exhibitor/announcements`, {
        type: form.type,
        title: form.title,
        body: form.body || undefined,
        image_url: form.image_url || undefined,
        discount_label: form.discount_label || undefined,
        discount_pct: form.discount_pct ? Number(form.discount_pct) : undefined,
        quantity_limit: form.quantity_limit ? Number(form.quantity_limit) : undefined,
        starts_at: toMysql(startsAt),
        expires_at: toMysql(expires),
        send_push: !!form.send_push,
        scheduled_at: scheduledDate ? toMysql(scheduledDate) : undefined,
        is_active: true,
      }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      const pushInfo = res.data?.push;
      if (pushInfo) {
        notify(t('exhibitor.announcements.push_sent_count', { count: pushInfo.sent }));
      } else if (form.send_push && scheduledDate && scheduledDate > now) {
        notify(t('exhibitor.announcements.scheduled_msg', { time: scheduledDate.toLocaleString() }));
      } else {
        notify(t('exhibitor.announcements.published'));
      }
      setForm((p) => ({ ...p, title: '', body: '', scheduled_at: '', image_url: '', quantity_limit: '' }));
      fetchList();
    } catch (e) {
      notify(e.response?.data?.message || 'Failed', true);
    } finally {
      setSaving(false);
    }
  };

  const patchImage = async (a, file) => {
    if (!file) return;
    if (file.size > 800 * 1024) { notify('Image too large — max 800KB', true); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.patch(`${API_URL}/exhibitor/announcements/${a.id}`,
          { image_url: reader.result },
          { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
        notify('Image updated');
        fetchList();
      } catch (e) {
        notify(friendlyError(e, 'Update failed', t), true);
      }
    };
    reader.readAsDataURL(file);
  };
  const removeImage = async (a) => {
    if (!window.confirm('Remove this image?')) return;
    try {
      await axios.patch(`${API_URL}/exhibitor/announcements/${a.id}`,
        { image_url: '' },
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      fetchList();
    } catch (e) { notify(friendlyError(e, 'Update failed', t), true); }
  };

  const toggleActive = async (a) => {
    try {
      await axios.patch(`${API_URL}/exhibitor/announcements/${a.id}`, { is_active: !a.is_active }, {
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
      });
      fetchList();
    } catch (e) {
      notify(e.response?.data?.message || 'Failed', true);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API_URL}/exhibitor/announcements/${id}`, { headers: authHeaders });
      setItems((prev) => prev.filter((x) => x.id !== id));
      notify('Deleted');
    } catch (e) {
      notify(e.response?.data?.message || 'Failed', true);
    }
  };

  const loadQr = async (a) => {
    if (qrCache[a.id]) return;
    try {
      const res = await axios.get(`${API_URL}/announcements/${a.id}/qr`);
      const sanitized = DOMPurify.sanitize(res.data.data.qr_svg, { USE_PROFILES: { svg: true, svgFilters: true } });
      setQrCache((prev) => ({ ...prev, [a.id]: sanitized }));
    } catch {
      // Not live (expired) — silently ignore
    }
  };

  return (
    <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-6 border border-slate-800 mt-6" data-testid="announcements-manager">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="w-4 h-4 text-yellow-400" />
        <div className="text-emerald-400 text-[10px] uppercase tracking-widest font-bold">Announcements & Coupons</div>
      </div>

      {flash && (
        <div className={`mb-3 px-3 py-2 rounded-lg text-xs font-semibold ${flash.isError ? 'bg-red-500/20 text-red-200 border border-red-500/40' : 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40'}`} data-testid="ann-flash">
          {flash.msg}
        </div>
      )}

      {/* Quick promo bar */}
      <div className="p-3 rounded-xl bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-red-500/10 border border-yellow-500/30 mb-6">
        <div className="text-yellow-300 text-[10px] uppercase font-black tracking-widest">Quick flash deal</div>
        <div className="text-white text-xs mb-2">Push a limited-time offer to every visitor's phone in one click.</div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={form.discount_label}
            onChange={(e) => setForm((p) => ({ ...p, discount_label: e.target.value }))}
            className="bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs"
            data-testid="quick-promo-label"
          >
            <option>BUY 1 FREE 1</option>
            <option>50% OFF</option>
            <option>30% OFF</option>
            <option>FREE SAMPLE</option>
            <option>RM10 OFF</option>
          </select>
          <input
            type="number"
            min={5}
            max={240}
            value={form.minutes}
            onChange={(e) => setForm((p) => ({ ...p, minutes: e.target.value }))}
            className="bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs w-20"
            data-testid="quick-promo-minutes"
          />
          <span className="text-slate-400 text-xs">{t('exhibitor.announcements.minutes')}</span>
          <button
            onClick={launchQuick}
            disabled={saving}
            className="ml-auto px-3 py-1.5 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-slate-900 text-xs font-black flex items-center gap-1.5 disabled:opacity-50"
            data-testid="launch-quick-promo-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} {t('exhibitor.announcements.launch')}
          </button>
        </div>
      </div>

      {/* Custom form */}
      <details className="mb-6 rounded-lg border border-slate-800 bg-slate-950/40" data-testid="custom-announcement-form">
        <summary className="cursor-pointer px-3 py-2 text-white text-xs font-bold flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> {t('exhibitor.announcements.custom')}
        </summary>
        <div className="p-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <select value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))} className={inputCls} data-testid="ann-type">
              <option value="flash_sale">{t('exhibitor.announcements.type_flash_sale')}</option>
              <option value="offer">{t('exhibitor.announcements.type_offer')}</option>
              <option value="announcement">{t('exhibitor.announcements.type_announcement')}</option>
            </select>
            <input value={form.discount_label} onChange={(e) => setForm((p) => ({ ...p, discount_label: e.target.value }))} placeholder={t('exhibitor.announcements.discount_label_placeholder')} className={inputCls} data-testid="ann-discount-label" />
          </div>
          <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder={t('exhibitor.announcements.title_placeholder')} className={inputCls} data-testid="ann-title" />
          <textarea value={form.body} onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} rows={3} placeholder={t('exhibitor.announcements.body_placeholder')} className={inputCls} data-testid="ann-body" />

          {/* Flash-sale image / banner */}
          <div className="flex items-center gap-3">
            {form.image_url ? (
              <img src={form.image_url} alt="banner preview" className="w-20 h-14 rounded-lg object-cover border border-slate-700" data-testid="ann-image-preview" />
            ) : (
              <div className="w-20 h-14 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center">
                <svg className="w-5 h-5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
              </div>
            )}
            <div className="flex-1">
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold cursor-pointer" data-testid="ann-image-upload">
                <Save className="w-3.5 h-3.5" /> Upload banner
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 800 * 1024) { notify('Image too large — max 800KB', true); return; }
                    const reader = new FileReader();
                    reader.onload = () => setForm((p) => ({ ...p, image_url: reader.result }));
                    reader.readAsDataURL(file);
                  }}
                  data-testid="ann-image-input"
                />
              </label>
              {form.image_url && (
                <button onClick={() => setForm((p) => ({ ...p, image_url: '' }))} className="ml-2 text-xs text-red-300 hover:underline">Clear</button>
              )}
              <div className="text-[10px] text-slate-500 mt-1">PNG/JPG/WebP · max 800KB · used in banners, cards & booth popup</div>
            </div>
          </div>

          {/* Push scheduling row */}
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-slate-200 text-xs bg-slate-950/60 border border-slate-700 rounded-lg px-3 py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.send_push}
                onChange={(e) => setForm((p) => ({ ...p, send_push: e.target.checked }))}
                className="w-4 h-4 accent-emerald-500"
                data-testid="ann-send-push"
              />
              <span className="flex-1">
                <span className="block text-white font-bold">📱 {t('exhibitor.announcements.send_push')}</span>
                <span className="block text-[10px] text-slate-400">{t('exhibitor.announcements.send_push_hint')}</span>
              </span>
            </label>
            <div>
              <label className="text-slate-400 text-[10px] uppercase font-bold tracking-widest block mb-1">{t('exhibitor.announcements.schedule_for')}</label>
              <input
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => setForm((p) => ({ ...p, scheduled_at: e.target.value }))}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs"
                data-testid="ann-scheduled-at"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">{t('exhibitor.announcements.schedule_hint')}</div>
            </div>
          </div>

          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="text-slate-400 text-[10px] uppercase font-bold tracking-widest block mb-1">{t('exhibitor.announcements.valid_for')}</label>
              <input type="number" min={5} max={720} value={form.minutes} onChange={(e) => setForm((p) => ({ ...p, minutes: e.target.value }))} className="bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs w-24" />
            </div>
            <span className="text-slate-400 text-xs mb-2">{t('exhibitor.announcements.minutes')}</span>
            <div>
              <label className="text-slate-400 text-[10px] uppercase font-bold tracking-widest block mb-1">Quantity limit</label>
              <input type="number" min={1} max={99999} value={form.quantity_limit} onChange={(e) => setForm((p) => ({ ...p, quantity_limit: e.target.value }))} placeholder="Unlimited" className="bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs w-28" data-testid="ann-quantity-limit" />
            </div>
            <button
              onClick={createCustom}
              disabled={saving}
              className="ml-auto px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
              data-testid="publish-announcement-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {t('exhibitor.announcements.publish')}
            </button>
          </div>
        </div>
      </details>

      {/* List */}
      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-xs text-center py-6">No announcements yet. Launch your first flash deal above.</div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="rounded-lg border border-slate-800 p-3 bg-slate-950/40" data-testid={`ann-row-${a.id}`}>
              <div className="flex items-start gap-3">
                {a.image_url && (
                  <img src={a.image_url} alt="banner" className="w-20 h-16 rounded-lg object-cover border border-slate-700 flex-none" data-testid={`ann-row-image-${a.id}`} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest ${
                      a.type === 'flash_sale' ? 'bg-red-500/20 text-red-300' :
                      a.type === 'offer' ? 'bg-emerald-500/20 text-emerald-300' :
                      'bg-blue-500/20 text-blue-300'
                    }`}>{a.type.replace('_', ' ')}</span>
                    {a.discount_label && (
                      <span className="text-[10px] font-black text-slate-900 bg-yellow-400 px-2 py-0.5 rounded-full">{a.discount_label}</span>
                    )}
                    <span className={`text-[10px] font-bold ${a.is_active ? 'text-emerald-400' : 'text-slate-500'}`}>
                      {a.is_active ? '● LIVE' : '○ paused'}
                    </span>
                  </div>
                  <div className="text-white font-bold text-sm">{a.title}</div>
                  {a.body && <div className="text-slate-300 text-xs mt-1">{a.body}</div>}
                  <div className="text-slate-500 text-[10px] mt-1">
                    Coupon: <span className="text-emerald-300 font-mono font-bold">{a.coupon_code}</span>
                    {a.redemption_count > 0 && <> · <span className="text-yellow-300 font-bold">{a.redemption_count} redeemed</span></>}
                    {a.expires_at && <> · expires {new Date(a.expires_at).toLocaleString()}</>}
                    {a.send_push && a.scheduled_at && !a.push_sent && (
                      <> · <span className="text-sky-300 font-bold">🕒 push at {new Date(a.scheduled_at).toLocaleString()}</span></>
                    )}
                    {a.send_push && a.push_sent && a.push_sent_at && (
                      <> · <span className="text-emerald-300 font-bold">📱 pushed {new Date(a.push_sent_at).toLocaleString()}</span></>
                    )}
                  </div>
                </div>
                {/* QR toggle */}
                <button
                  onClick={() => loadQr(a)}
                  className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200"
                  title="Show QR"
                  data-testid={`ann-qr-btn-${a.id}`}
                >
                  <QrIcon className="w-4 h-4" />
                </button>
              </div>

              {qrCache[a.id] && (
                <div className="mt-3 flex items-center gap-3">
                  <div
                    className="bg-white rounded-lg p-2 w-24 h-24 flex items-center justify-center"
                    data-testid={`ann-qr-${a.id}`}
                    dangerouslySetInnerHTML={{ __html: qrCache[a.id] }}
                  />
                  <div className="text-xs text-slate-400 flex-1">
                    Print this on your booth signage. Visitors scan → auto-open redeem page with coupon code <span className="font-mono text-emerald-300">{a.coupon_code}</span>.
                  </div>
                </div>
              )}

              <div className="mt-2 flex gap-2 flex-wrap">
                <button onClick={() => toggleActive(a)} className="px-2 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-white text-[11px] font-bold" data-testid={`ann-toggle-${a.id}`}>
                  {a.is_active ? 'Pause' : 'Resume'}
                </button>
                <label className="px-2 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-white text-[11px] font-bold flex items-center gap-1 cursor-pointer" title={a.image_url ? 'Change image' : 'Add image'} data-testid={`ann-image-btn-${a.id}`}>
                  📷 {a.image_url ? 'Change image' : 'Add image'}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => patchImage(a, e.target.files?.[0])} data-testid={`ann-image-input-${a.id}`} />
                </label>
                {a.image_url && (
                  <button onClick={() => removeImage(a)} className="px-2 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-white text-[11px] font-bold" data-testid={`ann-image-remove-${a.id}`}>
                    Remove image
                  </button>
                )}
                <button onClick={() => remove(a.id)} className="px-2 py-1 rounded-md bg-red-500/80 hover:bg-red-600 text-white text-[11px] font-bold flex items-center gap-1" data-testid={`ann-delete-${a.id}`}>
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {boothNumber && (
        <div className="mt-4 text-slate-500 text-[10px]">
          Announcements are attached to booth <span className="font-mono text-slate-300">{boothNumber}</span>.
        </div>
      )}
    </div>
  );
}

const inputCls = 'w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 rounded-lg px-3 py-2 text-white text-sm outline-none';

function toMysql(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * Turn an axios error into a human-friendly message.
 * Handles: Laravel validation (422), FastAPI proxy failures ({detail}),
 * network errors, and generic HTTP failures.
 * Optional `t` (translation function) enables localized fallbacks.
 */
function friendlyError(err, fallback = 'Something went wrong', t = null) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};

  // Laravel 422 → show the first field validation error
  if (status === 422 && data.errors) {
    const first = Object.values(data.errors)[0];
    if (Array.isArray(first) && first[0]) return first[0];
  }

  // FastAPI proxy → { detail: "Laravel backend unreachable: ..." }
  if (data.detail) {
    if (/unreachable|connection/i.test(data.detail)) {
      return t ? t('exhibitor.announcements.server_starting') : 'Server is starting up — please retry in a few seconds.';
    }
    return data.detail;
  }

  if (data.message) return data.message;

  // Network-level (no response at all)
  if (err && !err.response) {
    return t ? t('exhibitor.announcements.network_error') : 'Network error — check your connection and try again.';
  }

  return fallback;
}
